// AssetImage.tsx
import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { assetIconUrl } from '../api/assetIcons';

interface AssetImageProps {
    assetType?: string | null;
    ticker?: string | null;
    size?: number;
}

export function AssetImage({ assetType, ticker, size = 36 }: AssetImageProps) {
    const path =
        assetType && ticker ? `/api/asset-image/${assetType}/${ticker}` : null;

    // Keyed by path so a prop change cannot paint the previous asset's icon:
    // render runs before the effect, so plain state would still hold the old
    // URL for a frame.
    const [loaded, setLoaded] = useState<{ path: string; url: string } | null>(
        null
    );
    const [failedPath, setFailedPath] = useState<string | null>(null);

    // The bytes have to come through the API client rather than an <img src>:
    // the proxy in front of the indexer authenticates on the Authorization
    // header only, and a browser never sends one for an image subresource, so
    // a direct src lands on 401 sign_in_required.
    useEffect(() => {
        if (!path) return;
        let cancelled = false;
        assetIconUrl(path)
            .then(url => {
                if (!cancelled) setLoaded({ path, url });
            })
            .catch(() => {
                if (!cancelled) setFailedPath(path);
            });
        return () => {
            cancelled = true;
        };
    }, [path]);

    if (!path || failedPath === path) return null;

    const sx = {
        width: size,
        height: size,
        borderRadius: '8px',
        objectFit: 'cover' as const,
        flexShrink: 0,
        ...(assetType === 'commodity' && {
            border: '1px solid #f5f5f5',
        }),
    };

    const src = loaded?.path === path ? loaded.url : null;

    // Hold the space while the bytes are in flight. Rendering nothing would
    // let every row reflow as its icon lands, jolting a whole card grid.
    if (!src) return <Box sx={sx} />;

    return (
        <Box
            component="img"
            src={src}
            onError={() => setFailedPath(path)}
            sx={sx}
        />
    );
}
